import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { useColorScheme } from "@mui/material/styles";
import { fluid, marcaFija } from "../theme/theme";

/**
 * Variante en la que el símbolo hace de «A» inicial del nombre.
 *
 * El logotipo ya es una A, así que en lugar de repetirla al lado del texto se
 * usa como primera letra: se lee «Atlas» una sola vez y la composición queda
 * más compacta. Debajo, el descriptor.
 *
 * `align` existe porque la misma marca vive en dos sitios con dos ejes: en el
 * acceso de móvil va centrada bajo el logotipo, y en el panel de escritorio
 * comparte eje izquierdo con el titular y el resto de la columna.
 *
 * El texto visible es solo «tlas», que leído en voz alta no significa nada:
 * el nombre completo se declara en el `aria-label` del conjunto y las piezas
 * se ocultan al lector de pantalla.
 */
/**
 * Dónde está la A dentro de cada archivo, medido leyendo el canal alfa de los
 * propios PNG.
 *
 * No son proporciones inventadas. El violeta trae 110 px transparentes por
 * los **cuatro** lados de un lienzo de 662×685; el blanco viene recortado a
 * hueso y llena su lienzo entero. Por eso el mismo multiplicador daba dos
 * tamaños distintos según el fondo, y por eso hace falta saber también dónde
 * cae el borde de abajo: la línea base de una imagen es su borde inferior, y
 * si ahí hay 110 px de nada, la A se apoya en el aire.
 */
const GLIFO = {
  claro: { lienzo: [662, 685], caja: [110, 110, 550, 574] },
  oscuro: { lienzo: [212, 224], caja: [0, 0, 211, 223] },
};

/**
 * Convierte el recorte del archivo en una caja que contiene **solo** la A.
 *
 * El primer intento fue dejar la imagen entera y bajarla con un margen
 * inferior negativo. No funciona, y el motivo merece anotarse: en una línea
 * de texto, la línea base de una imagen es su borde de margen, así que un
 * margen negativo la desplaza; pero dentro de un contenedor flexible —que es
 * lo que hay aquí— la línea base sintética se toma de la **caja de borde**, y
 * los márgenes dejan de contar. Medido: el margen se aplicaba, y el borde
 * inferior de la imagen seguía clavado en la línea base con la A flotando
 * nueve píxeles por encima.
 *
 * La salida es no pelearse con la alineación: se pinta el archivo como fondo
 * de una caja del tamaño exacto del glifo, desplazado para que la A quede
 * dentro. Así el borde de la caja **es** el contorno de la A, y apoyarla en
 * la línea base deja de necesitar correcciones: cuadra sola, con este archivo
 * y con cualquier otro que se recorte distinto.
 *
 * Todo en unidades de cuerpo de letra, para que siga al tamaño del texto.
 */
function cajaDelGlifo(glifo, cap) {
  const [ancho, alto] = glifo.lienzo;
  const [x0, y0, x1, y1] = glifo.caja;
  const visibleAncho = x1 - x0 + 1;
  const visibleAlto = y1 - y0 + 1;

  const cajaAlto = cap;
  const cajaAncho = cap * (visibleAncho / visibleAlto);
  return {
    cajaAlto,
    cajaAncho,
    fondoAncho: cajaAncho * (ancho / visibleAncho),
    fondoAlto: cajaAlto * (alto / visibleAlto),
    desplazaX: -(x0 / ancho) * cajaAncho * (ancho / visibleAncho),
    desplazaY: -(y0 / alto) * cajaAlto * (alto / visibleAlto),
  };
}

/**
 * Cuánto levanta una mayúscula en Lexend 600, medido con `TextMetrics`.
 *
 * Es la referencia correcta para este símbolo: el logotipo es una A, y en
 * tipografía una A mide una mayúscula, no un cuerpo. Antes se escalaba a 1,02
 * veces el cuerpo —es decir, a la caja tipográfica entera— y salía un 46 % más
 * alta que las letras que tiene al lado, que es lo que la hacía parecer un
 * icono pegado en vez de la primera letra de la palabra.
 */
const ALTURA_DE_MAYUSCULA = 0.7;

/**
 * Dos tallas, no una escala.
 *
 * La misma marca vive en el acceso, donde sostiene media pantalla, y en el
 * menú lateral, donde es un membrete de 268 px. Multiplicar la talla grande
 * por un factor no servía: el descriptor iba a `fluid(12, 14.5)`, y encogido
 * a un tercio caían cuatro píxeles de letra, ilegibles. Cada sitio declara
 * lo suyo.
 */
const TALLAS = {
  portada: (escala) => ({
    cuerpo: fluid(54 * escala, 68 * escala),
    descriptor: fluid(12 * escala, 14.5 * escala),
    separacion: "10px",
  }),
  menu: (escala = 1) => ({
    cuerpo: `${2.2 * escala}rem`,
    descriptor: "0.62rem",
    separacion: "4px",
  }),
  /*
   * La barra del móvil.
   *
   * Antes aquí iba solo el símbolo metido en una cajita con borde, y pasaban
   * dos cosas: la cajita se leía como un botón que no lleva a ninguna parte, y
   * el símbolo salía a dos tamaños distintos según el tema —al 72 % de la caja
   * en claro y al 100 % en oscuro—, así que el mismo logotipo parecía crecer
   * al cambiar de modo.
   *
   * Con la composición de letra capitular el tamaño lo fija la altura de
   * mayúscula, que se mide igual en los dos archivos, y el símbolo deja de
   * necesitar un fondo que lo separe: lo separa la palabra que tiene al lado.
   */
  barra: () => ({
    // En el portal autenticado es la única firma de marca de la cabecera
    // móvil. Un pequeño aumento le devuelve jerarquía sin ensanchar la barra
    // ni competir con las tres acciones del extremo derecho.
    cuerpo: "2rem",
    descriptor: "0.55rem",
    separacion: "2px",
  }),
};

/**
 * ¿Toca el glifo blanco?
 *
 * `onDark` dice «voy encima de una superficie oscura de marca» —el panel
 * violeta del acceso—, que es una decisión de composición y no del tema. Pero
 * con el portal en oscuro el menú lateral también es oscuro, y allí el
 * logotipo violeta se hunde en el fondo. Son dos motivos distintos para usar
 * la versión recortada y teñida de blanco, y valen los dos.
 *
 * `esquema` cubre el tercer caso: el acceso va siempre en claro aunque el
 * sistema esté en oscuro, y sin poder decirlo pintaría la A en blanco sobre
 * su propio fondo blanco.
 */
function useGlifoBlanco(onDark, esquema) {
  const { mode, systemMode } = useColorScheme();
  if (onDark) return true;
  if (esquema) return esquema === "oscuro";
  return (mode === "system" ? systemMode : mode) === "dark";
}

function BrandLettermark({
  onDark,
  scale,
  align = "center",
  talla = "portada",
  esquema,
  descriptor = true,
  colorTexto = "texto",
  showBeta = false,
  glifoOpaco = false,
}) {
  const { t } = useTranslation();
  const enBlanco = useGlifoBlanco(onDark, esquema);
  const nombre = t("brand.name");
  const medidas = (TALLAS[talla] ?? TALLAS.portada)(scale);
  const cuerpo = medidas.cuerpo;

  const caja = cajaDelGlifo(
    enBlanco ? GLIFO.oscuro : GLIFO.claro,
    ALTURA_DE_MAYUSCULA,
  );
  // Lo único que queda a ojo: un pelo de acercamiento para que la A y la «t»
  // se toquen como dos letras de la misma palabra.
  const APRIETE = 0.015;

  return (
    <Stack
      role="img"
      aria-label={`${nombre} · ${t("brand.portal")}${showBeta ? " · Beta" : ""}`}
      spacing={0}
      sx={{ alignItems: align }}
    >
      <Stack
        direction="row"
        spacing={0}
        aria-hidden
        // Las letras se apoyan en la misma línea base que el símbolo.
        sx={{ alignItems: "baseline" }}
      >
        <Box
          component="span"
          aria-hidden
          sx={{
            // La caja mide exactamente la A: alto de mayúscula y el ancho que
            // le corresponda. El archivo va de fondo, ampliado y corrido lo
            // justo para que el glifo caiga dentro.
            display: "block",
            flexShrink: 0,
            height: `calc(${cuerpo} * ${caja.cajaAlto.toFixed(4)})`,
            width: `calc(${cuerpo} * ${caja.cajaAncho.toFixed(4)})`,
            // El archivo claro lleva la A al 77 % de opacidad; con `glifoOpaco`
            // se usa la misma A sin transparencia (sobre la esfera de Atlas).
            backgroundImage: `url(${
              enBlanco ? "/adaltra-logo-dark.png" : glifoOpaco ? "/adaltra-logo-opaco.png" : "/adaltra-logo.png"
            })`,
            backgroundRepeat: "no-repeat",
            backgroundSize: `calc(${cuerpo} * ${caja.fondoAncho.toFixed(4)}) calc(${cuerpo} * ${caja.fondoAlto.toFixed(4)})`,
            backgroundPosition: `calc(${cuerpo} * ${caja.desplazaX.toFixed(4)}) calc(${cuerpo} * ${caja.desplazaY.toFixed(4)})`,
            // El apriete se mide en cuerpos, no en `em`: así sigue al tamaño
            // del texto y no al que haya heredado el contexto.
            marginRight: `calc(${cuerpo} * -${APRIETE})`,
            // Ahora que la caja es el glifo, apoyarla en la línea base ya la
            // deja donde tiene que estar.
            alignSelf: "baseline",
            ...(enBlanco ? { filter: "brightness(0) invert(1)" } : {}),
          }}
        />
        <Typography
          component="span"
          sx={{
            fontSize: cuerpo,
            fontWeight: 600,
            lineHeight: 1,
            letterSpacing: "-0.02em",
            /*
             * Con `colorTexto="marca"` la palabra toma el color del propio
             * logotipo —el violeta #463a86 del archivo claro, blanco cuando
             * se usa la versión teñida—, de modo que la A y las cuatro letras
             * que la siguen se lean como una sola pieza y no como un símbolo
             * con una palabra al lado.
             */
            color:
              colorTexto === "marca"
                ? enBlanco || onDark
                  ? "#fff"
                  : marcaFija.violet
                : onDark
                  ? "#fff"
                  : "text.primary",
          }}
        >
          {nombre.slice(1)}
        </Typography>
        {showBeta && (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              // En PC queda centrado; en la cabecera móvil funciona mejor
              // arriba, como un distintivo en superíndice.
              alignSelf: talla === "barra" ? "flex-start" : "center",
              mt: talla === "barra" ? 0.25 : 0,
              height: talla === "barra" ? 13 : 14,
              ml: talla === "barra" ? 0.75 : 1,
              px: talla === "barra" ? 0.4 : 0.5,
              borderRadius: 99,
              bgcolor: enBlanco ? "rgba(255,255,255,0.12)" : marcaFija.violet,
              border: "1px solid",
              borderColor: enBlanco
                ? "rgba(255,255,255,0.6)"
                : marcaFija.violet,
              color: "#fff",
              fontSize: talla === "barra" ? "0.4rem" : "0.44rem",
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
            }}
          >
            Beta
          </Box>
        )}
      </Stack>

      {descriptor && (
      <Typography
        component="span"
        aria-hidden
        sx={{
          mt: medidas.separacion,
          fontSize: medidas.descriptor,
          fontWeight: 700,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          lineHeight: 1.2,
          textAlign: align === "center" ? "center" : "left",
          color: onDark ? "rgba(255,255,255,0.62)" : "text.secondary",
        }}
      >
        {t("brand.portal")}
      </Typography>
      )}
    </Stack>
  );
}

/**
 * Marca del portal: símbolo, nombre y descriptor.
 *
 * Sobre fondo oscuro se usa la versión recortada del símbolo teñida de blanco
 * (`brightness(0) invert(1)`). El logotipo de marca es violeta y sobre un
 * fondo oscuro apenas se despegaba; antes se compensaba con un halo blanco
 * que lo hacía parecer una pegatina.
 *
 * El nombre manda y el descriptor acompaña: va en versales pequeñas, el mismo
 * recurso tipográfico que usan los antetítulos del resto del portal, en lugar
 * de competir con el nombre a un cuerpo casi igual de grande.
 *
 * Variantes: `stacked` centra el símbolo sobre el texto; `showMark` permite
 * prescindir del símbolo donde el contexto ya es inequívoco, y `scale` agranda
 * el conjunto para que el nombre sostenga la composición él solo.
 */
export function Brand({
  compact = false,
  hero = false,
  onDark = false,
  stacked = false,
  showMark = true,
  scale = 1,
  lettermark = false,
  align = "center",
  talla = "portada",
  descriptor = true,
  colorTexto = "texto",
  showBeta = false,
  glifoOpaco = false,
  esquema,
}) {
  const { t } = useTranslation();
  const enBlanco = useGlifoBlanco(onDark, esquema);

  if (lettermark) {
    return (
      <BrandLettermark
        onDark={onDark}
        scale={scale}
        align={align}
        talla={talla}
        esquema={esquema}
        descriptor={descriptor}
        colorTexto={colorTexto}
        showBeta={showBeta}
        glifoOpaco={glifoOpaco}
      />
    );
  }

  // Cada archivo tiene un recorte distinto: el violeta lleva mucho aire
  // alrededor, así que necesita más caja para verse del mismo tamaño.
  //
  // La variante grande subió de cuerpo: en el panel de acceso la marca se
  // quedaba pequeña para el sitio que tiene, y era lo primero que se lee de
  // toda la pantalla. El símbolo acompaña en la misma proporción para que no
  // se descuelgue del texto.
  const markSize = hero
    ? onDark
      ? fluid(58 * scale, 78 * scale)
      : fluid(70 * scale, 95 * scale)
    : onDark
      ? 26
      : 38;

  const nameSize = hero ? fluid(29 * scale, 40 * scale) : "0.95rem";
  const descriptorSize = hero ? fluid(12 * scale, 14.5 * scale) : "0.62rem";

  return (
    <Stack
      direction={stacked ? "column" : "row"}
      spacing={stacked ? 1.75 : hero ? 2 : 1.5}
      sx={{ alignItems: "center" }}
    >
      {showMark && (
        <Box
          component="span"
          sx={{
            width: markSize,
            height: markSize,
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            // El símbolo va suelto salvo en la barra lateral clara, donde la
            // cajita lo separa del fondo.
            ...(hero || onDark
              ? {}
              : {
                  borderRadius: 2.5,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                }),
          }}
        >
          <Box
            component="img"
            src={enBlanco ? "/adaltra-logo-dark.png" : "/adaltra-logo.png"}
            alt=""
            sx={{
              width: hero || enBlanco ? "100%" : "72%",
              height: hero || enBlanco ? "100%" : "72%",
              objectFit: "contain",
              ...(enBlanco ? { filter: "brightness(0) invert(1)" } : {}),
            }}
          />
        </Box>
      )}

      {!compact && (
        <Stack
          spacing={0}
          sx={{ minWidth: 0, alignItems: stacked ? "center" : "flex-start" }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: nameSize,
              fontWeight: 600,
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
              color: onDark ? "#fff" : "text.primary",
            }}
          >
            {t("brand.name")}
          </Typography>
          <Typography
            component="span"
            sx={{
              mt: hero ? "7px" : "3px",
              fontSize: descriptorSize,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              lineHeight: 1.2,
              color: onDark ? "rgba(255,255,255,0.62)" : "text.secondary",
            }}
          >
            {t("brand.portal")}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
